import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  FileDown,
  Calendar,
  Briefcase,
  Gift,
  Layers,
  DollarSign,
  MessageCircle,
  Loader2,
  Pencil,
  UserPlus,
  CreditCard,
  FileDigit,
  Trash2,
  Sparkles,
  User,
  Phone,
  MapPin,
  SearchX,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import {
  getOrderByIdAPI,
  shareOrderOnWhatsAppAPI,
  deleteOrderAPI,
} from "../../../Redux/ApiCalls/Dashboard/OrderAPI";
import { createInvoiceFromOrderAPI } from "../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { useAppSelector } from "../../../Redux/Hooks";
import { DocumentPrintTemplate } from "./DocumentPrintTemplate";
import { formatTimeToAMPM } from "../../../Utils/helper";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import OrderShareModal from "./OrderShareModal";
import MarketplaceEmptyState, {
  MarketplaceLoadingSpinner,
} from "./Marketplace/MarketplaceEmptyState";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { StatusBadge } from "./documentSharedStyles";
import {
  TableWrap,
  PaymentsTable,
  AmountCell,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import { device } from "../../atoms/Utlis/media";

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const [modalShareOrder, setModalShareOrder] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const calculatedGrandTotal = useMemo(() => {
    if (!order) return 0;
    if (order.without_price) return 0;
    let resourcesSum = 0;
    if (order.resources) {
      resourcesSum = order.resources.reduce(
        (sum: number, r: any) =>
          sum + (Number(r.unit_price) || 0) * (Number(r.quantity) || 0),
        0
      );
    }
    let fixChargesSum = 0;
    if (order.fix_charge_items) {
      fixChargesSum = order.fix_charge_items.reduce(
        (sum: number, fc: any) =>
          sum + (Number(fc.unit_price) || 0) * (Number(fc.quantity) || 0),
        0
      );
    }
    const totalAmount = resourcesSum + fixChargesSum;
    const discountAmount =
      (totalAmount * (Number(order.discount_percent) || 0)) / 100;
    const subtotal = totalAmount - discountAmount;
    const taxAmount = (subtotal * (Number(order.tax_percent) || 0)) / 100;
    return subtotal + taxAmount;
  }, [order]);

  const { user, studio_logo, business } = useAppSelector((state) => state.auth);

  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get("id");

  useEffect(() => {
    const fetchDetails = async () => {
      if (orderId) {
        try {
          const res = await getOrderByIdAPI(orderId);
          setOrder(res);
        } catch {
          showErrorToast("Failed to fetch order details");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [orderId]);

  const handleExportCSV = () => {
    if (!order) return;
    let csv = `Order Details\n`;
    csv += `Order No,${order.order_no}\n`;
    csv += `Date,${order.order_date}\n`;
    csv += `Customer Name,${order.customer_name}\n`;
    csv += `Customer Phone,${order.customer_phone}\n`;
    csv += `Place,${order.customer_place || ""}\n\n`;

    if (order.events && order.events.length > 0) {
      const hasAnyDate = order.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
      const hasAnyTime = order.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
      csv += `EVENTS\n`;
      const eventHeaders: string[] = [];
      if (hasAnyDate) eventHeaders.push("Date");
      if (hasAnyTime) eventHeaders.push("Time");
      eventHeaders.push("Event Type");
      csv += `${eventHeaders.join(",")}\n`;
      order.events.forEach((e: any) => {
        const row: string[] = [];
        if (hasAnyDate) row.push(`"${e.event_date || ""}"`);
        if (hasAnyTime) row.push(`"${formatTimeToAMPM(e.event_time)}"`);
        row.push(`"${e.event_type || ""}"`);
        csv += `${row.join(",")}\n`;
      });
      csv += `\n`;
    }

    if (order.resources && order.resources.length > 0) {
      csv += `RESOURCES / PRODUCTS\n`;
      csv += `Resource Name,Quantity,Price,Total\n`;
      order.resources.forEach((r: any) => {
        csv += `"${r.resource_name || ""}","${r.quantity}","${r.unit_price}","${(r.quantity * r.unit_price).toFixed(2)}"\n`;
      });
      csv += `\n`;
    }

    if (order.fix_charge_items && order.fix_charge_items.length > 0) {
      csv += `FIXED CHARGES\n`;
      csv += `Charge Name,Quantity,Price,Total\n`;
      order.fix_charge_items.forEach((fc: any) => {
        csv += `"${fc.charge_name || ""}","${fc.quantity}","${fc.unit_price}","${(fc.quantity * fc.unit_price).toFixed(2)}"\n`;
      });
      csv += `\n`;
    }

    if (order.gifts && order.gifts.length > 0) {
      csv += `GIFTS\n`;
      csv += `Gift Item,Quantity\n`;
      order.gifts.forEach((g: any) => {
        csv += `"${g.gift_name || ""}","${g.quantity}"\n`;
      });
      csv += `\n`;
    }

    if (order.album_detail) {
      csv += `ALBUM DETAILS\n`;
      csv += `Album Type,${order.album_detail.album_type_name || ""}\n`;
      csv += `Width x Height,${order.album_detail.album_width || ""} x ${order.album_detail.album_height || ""} inches\n`;
      csv += `Album Paper,${order.album_detail.album_paper_name || ""}\n`;
      csv += `Album Pages,${order.album_detail.album_pages || ""}\n`;
      csv += `\n`;
    }

    if (order.product_info_description) {
      const escapedNotes = `"${order.product_info_description.replace(/"/g, '""')}"`;
      csv += `PRODUCT INFORMATION / DESCRIPTION NOTES\n`;
      csv += `${escapedNotes}\n`;
      csv += `\n`;
    }

    if (order.album_detail) {
      const opts = order.album_detail;
      const hasOpts =
        opts.opt_album_page_cost ||
        Number(opts.opt_album_page_price) > 0 ||
        opts.opt_video_cost ||
        Number(opts.opt_video_price) > 0 ||
        Number(opts.opt_pad_price_from) > 0 ||
        Number(opts.opt_pad_price_to) > 0;
      if (hasOpts) {
        csv += `OPTIONAL SERVICES\n`;
        csv += `Service,Details / Price\n`;
        if (opts.opt_album_page_cost || Number(opts.opt_album_page_price) > 0) {
          const priceStr =
            Number(opts.opt_album_page_price) > 0
              ? `₹${opts.opt_album_page_price}`
              : "-";
          csv += `"Album Extra Page Sheet: ${opts.opt_album_page_cost || "Extra Sheet"}","${priceStr}"\n`;
        }
        if (opts.opt_video_cost || Number(opts.opt_video_price) > 0) {
          const priceStr =
            Number(opts.opt_video_price) > 0 ? `₹${opts.opt_video_price}` : "-";
          csv += `"Extra Video: ${opts.opt_video_cost || "Extra Video"}","${priceStr}"\n`;
        }
        if (
          Number(opts.opt_pad_price_from) > 0 ||
          Number(opts.opt_pad_price_to) > 0
        ) {
          csv += `"Album Pad Price Range","₹${opts.opt_pad_price_from || 0} TO ₹${opts.opt_pad_price_to || 0}"\n`;
        }
        csv += `\n`;
      }
    }

    csv += `PRICING\n`;
    csv += `Discount %,${order.discount_percent || 0}\n`;
    csv += `Tax %,${order.tax_percent || 0}\n`;
    csv += `Grand Total,₹${calculatedGrandTotal.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Order_${order.order_no}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("CSV exported successfully");
  };

  const handleExportExcel = () => {
    if (!order) return;
    let xls = `Order Details\t${order.order_no}\n`;
    xls += `Date\t${order.order_date}\n`;
    xls += `Customer Name\t${order.customer_name}\n`;
    xls += `Customer Phone\t${order.customer_phone}\n`;
    xls += `Place\t${order.customer_place || ""}\n\n`;

    if (order.events && order.events.length > 0) {
      const hasAnyDate = order.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
      const hasAnyTime = order.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
      xls += `EVENTS\n`;
      const eventHeaders: string[] = [];
      if (hasAnyDate) eventHeaders.push("Date");
      if (hasAnyTime) eventHeaders.push("Time");
      eventHeaders.push("Event Type");
      xls += `${eventHeaders.join("\t")}\n`;
      order.events.forEach((e: any) => {
        const row: string[] = [];
        if (hasAnyDate) row.push(e.event_date || "");
        if (hasAnyTime) row.push(formatTimeToAMPM(e.event_time));
        row.push(e.event_type || "");
        xls += `${row.join("\t")}\n`;
      });
      xls += `\n`;
    }

    if (order.resources && order.resources.length > 0) {
      xls += `RESOURCES / PRODUCTS\n`;
      xls += `Resource Name\tQuantity\tPrice\tTotal\n`;
      order.resources.forEach((r: any) => {
        xls += `${r.resource_name || ""}\t${r.quantity}\t${r.unit_price}\t${(r.quantity * r.unit_price).toFixed(2)}\n`;
      });
      xls += `\n`;
    }

    if (order.fix_charge_items && order.fix_charge_items.length > 0) {
      xls += `FIXED CHARGES\n`;
      xls += `Charge Name\tQuantity\tPrice\tTotal\n`;
      order.fix_charge_items.forEach((fc: any) => {
        xls += `${fc.charge_name || ""}\t${fc.quantity}\t${fc.unit_price}\t${(fc.quantity * fc.unit_price).toFixed(2)}\n`;
      });
      xls += `\n`;
    }

    if (order.gifts && order.gifts.length > 0) {
      xls += `GIFTS\n`;
      xls += `Gift Item\tQuantity\n`;
      order.gifts.forEach((g: any) => {
        xls += `${g.gift_name || ""}\t${g.quantity}\n`;
      });
      xls += `\n`;
    }

    if (order.album_detail) {
      xls += `ALBUM DETAILS\n`;
      xls += `Album Type\t${order.album_detail.album_type_name || ""}\n`;
      xls += `Width x Height\t${order.album_detail.album_width || ""} x ${order.album_detail.album_height || ""} inches\n`;
      xls += `Album Paper\t${order.album_detail.album_paper_name || ""}\n`;
      xls += `Album Pages\t${order.album_detail.album_pages || ""}\n\n`;
    }

    if (order.product_info_description) {
      xls += `PRODUCT INFORMATION / DESCRIPTION NOTES\n`;
      xls += `"${order.product_info_description.replace(/"/g, '""')}"\n\n`;
    }

    if (order.album_detail) {
      const opts = order.album_detail;
      const hasOpts =
        opts.opt_album_page_cost ||
        Number(opts.opt_album_page_price) > 0 ||
        opts.opt_video_cost ||
        Number(opts.opt_video_price) > 0 ||
        Number(opts.opt_pad_price_from) > 0 ||
        Number(opts.opt_pad_price_to) > 0;
      if (hasOpts) {
        xls += `OPTIONAL SERVICES\n`;
        xls += `Service\tDetails / Price\n`;
        if (opts.opt_album_page_cost || Number(opts.opt_album_page_price) > 0) {
          const priceStr =
            Number(opts.opt_album_page_price) > 0
              ? `₹${opts.opt_album_page_price}`
              : "-";
          xls += `Album Extra Page Sheet: ${opts.opt_album_page_cost || "Extra Sheet"}\t${priceStr}\n`;
        }
        if (opts.opt_video_cost || Number(opts.opt_video_price) > 0) {
          const priceStr =
            Number(opts.opt_video_price) > 0 ? `₹${opts.opt_video_price}` : "-";
          xls += `Extra Video: ${opts.opt_video_cost || "Extra Video"}\t${priceStr}\n`;
        }
        if (
          Number(opts.opt_pad_price_from) > 0 ||
          Number(opts.opt_pad_price_to) > 0
        ) {
          xls += `Album Pad Price Range\t₹${opts.opt_pad_price_from || 0} TO ₹${opts.opt_pad_price_to || 0}\n`;
        }
        xls += `\n`;
      }
    }

    xls += `PRICING\n`;
    xls += `Discount %\t${order.discount_percent || 0}\n`;
    xls += `Tax %\t${order.tax_percent || 0}\n`;
    xls += `Grand Total\t₹${calculatedGrandTotal.toFixed(2)}\n`;

    const blob = new Blob([xls], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Order_${order.order_no}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showSuccessToast("Excel exported successfully");
  };

  const generatePdfBlob = async (): Promise<{ blob: Blob; filename: string }> => {
    const element = printRef.current;
    if (!element) throw new Error("Print template not ready");

    const blob = await exportElementAsPDF(element);
    const filename = `Order_${order?.order_no}.pdf`;
    return { blob, filename };
  };

  const handleExportPDF = async () => {
    if (!order) return;
    try {
      const { blob, filename } = await generatePdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const handleShareWhatsApp = () => {
    if (!order) return;
    setModalShareOrder({
      ...order,
      grand_total: String(calculatedGrandTotal),
    });
  };

  const handleOfficialShare = async () => {
    if (!order) return;
    setSharingWhatsApp(true);
    try {
      const { blob, filename } = await generatePdfBlob();
      const result = await shareOrderOnWhatsAppAPI(order.id, blob, filename);

      showSuccessToast(
        `Order sent to ${result.sent_to || order.customer_phone} on WhatsApp 🎉`
      );
    } catch (err: any) {
      showErrorToast(
        err?.message || "Failed to share on WhatsApp. Please try again."
      );
    } finally {
      setSharingWhatsApp(false);
      setModalShareOrder(null);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  const handleConfirmDelete = async () => {
    if (!order) return;
    try {
      await deleteOrderAPI(order.id);
      showSuccessToast("Order deleted successfully");
      navigate("/dashboard/orders/all");
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete order"));
    } finally {
      setDeleteConfirm(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!order) return;
    setCreatingInvoice(true);
    try {
      const res = await createInvoiceFromOrderAPI({ order_id: Number(order.id) });
      showSuccessToast("Invoice drafted from order successfully");
      navigate(`/dashboard/invoice/edit?id=${res.id}`);
    } catch (err: any) {
      const errorMsg = getApiErrorMessage(err, "Failed to create invoice from order");
      if (errorMsg.toLowerCase().includes("already exists")) {
        showErrorToast("Invoice for this order is already created");
      } else {
        showErrorToast(errorMsg);
      }
    } finally {
      setCreatingInvoice(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <MarketplaceLoadingSpinner padding="80px 0" />
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <Hero>
          <HeroGlow />
          <HeroContent>
            <HeroLeft>
              <BackBtn
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </BackBtn>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={13} />
                  Order
                </HeroBadge>
                <PageTitle className="linear-text">Order Details</PageTitle>
              </HeroText>
            </HeroLeft>
          </HeroContent>
        </Hero>
        <MarketplaceEmptyState
          icon={<SearchX size={36} strokeWidth={1.5} />}
          title="No order data found"
          description="This order may have been deleted or the link is invalid."
          actions={[
            {
              label: "Back to orders",
              onClick: () => navigate("/dashboard/orders/all"),
            },
          ]}
        />
      </PageContainer>
    );
  }

  const phoneIsMissing = !order.customer_phone?.trim();
  const whatsappTooltip = phoneIsMissing
    ? "Add customer phone number before sharing on WhatsApp"
    : "Share order PDF on WhatsApp";

  const hasOptionalServices =
    order.album_detail &&
    (order.album_detail.opt_album_page_cost ||
      Number(order.album_detail.opt_album_page_price) > 0 ||
      order.album_detail.opt_video_cost ||
      Number(order.album_detail.opt_video_price) > 0 ||
      Number(order.album_detail.opt_pad_price_from) > 0 ||
      Number(order.album_detail.opt_pad_price_to) > 0);

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                Order {order.order_no}
              </HeroBadge>
              <TitleRow>
                <PageTitle className="linear-text">Order Details</PageTitle>
                {order.status && (
                  <StatusBadge $status={order.status}>
                    {order.status}
                  </StatusBadge>
                )}
              </TitleRow>
              <PageSubtitle>
                View and export details for order {order.order_no}.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
        </HeroContent>
      </Hero>

      <ScrollBody>
        <SectionCard>
          <ActionRow>
            <ActionGroup>
              <SoftBtn $tone="#35b7c8" type="button" onClick={handleExportCSV}>
                <FileText size={15} strokeWidth={2.2} />
                CSV
              </SoftBtn>
              <SoftBtn $tone="#31a66a" type="button" onClick={handleExportExcel}>
                <FileSpreadsheet size={15} strokeWidth={2.2} />
                Excel
              </SoftBtn>
              <SoftBtn $tone="#e44862" type="button" onClick={handleExportPDF}>
                <FileDown size={15} strokeWidth={2.2} />
                PDF
              </SoftBtn>
            </ActionGroup>

            <ActionDivider />

            <ActionGroup>
              <SoftBtn
                $tone="#25d366"
                type="button"
                disabled={phoneIsMissing || sharingWhatsApp}
                onClick={handleShareWhatsApp}
                title={whatsappTooltip}
              >
                {sharingWhatsApp ? (
                  <SpinIcon size={15} />
                ) : (
                  <MessageCircle size={15} strokeWidth={2.2} />
                )}
                {sharingWhatsApp ? "Sending..." : "WhatsApp"}
              </SoftBtn>
            </ActionGroup>

            <ActionSpacer />

            <ActionGroup>
              <PermissionGate permission="orders.update">
                <SoftBtn
                  $tone="#c94daa"
                  type="button"
                  onClick={() =>
                    navigate(`/dashboard/orders/create?id=${order.id}`)
                  }
                >
                  <Pencil size={15} strokeWidth={2.2} />
                  Edit
                </SoftBtn>
              </PermissionGate>
              <PermissionGate permission="orders.update">
                <SoftBtn
                  $tone="#6366f1"
                  type="button"
                  onClick={() =>
                    navigate(`/dashboard/orders/work?orderNo=${order.order_no}`)
                  }
                >
                  <UserPlus size={15} strokeWidth={2.2} />
                  Assign Work
                </SoftBtn>
              </PermissionGate>
              <SoftBtn
                $tone="#f09a3e"
                type="button"
                onClick={() =>
                  navigate(
                    `/dashboard/orders/transactions?orderNo=${order.order_no}`
                  )
                }
              >
                <CreditCard size={15} strokeWidth={2.2} />
                Payment
              </SoftBtn>
              <PermissionGate anyPermission={["invoice.create", "payments_management.create"]}>
                <SoftBtn
                  $tone="#35b7c8"
                  type="button"
                  onClick={handleCreateInvoice}
                  disabled={creatingInvoice}
                >
                  {creatingInvoice ? (
                    <SpinIcon size={15} />
                  ) : (
                    <FileDigit size={15} strokeWidth={2.2} />
                  )}
                  {creatingInvoice ? "Creating..." : "Invoice"}
                </SoftBtn>
              </PermissionGate>
              <PermissionGate permission="orders.delete">
                <SoftBtn
                  $tone="#e44862"
                  $solid
                  type="button"
                  onClick={() => setDeleteConfirm(true)}
                >
                  <Trash2 size={15} strokeWidth={2.2} />
                  Delete
                </SoftBtn>
              </PermissionGate>
            </ActionGroup>
          </ActionRow>

          <CardHeader>
            <CardIcon $tone="#c94daa">
              <Briefcase size={18} />
            </CardIcon>
            <div>
              <CardTitle>Basic Information</CardTitle>
              <CardSubtitle>Customer and order metadata</CardSubtitle>
            </div>
          </CardHeader>

          <InfoGrid>
            <InfoItem>
              <InfoLabel>
                <User size={12} /> Customer Name
              </InfoLabel>
              <InfoValue>{order.customer_name}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <Phone size={12} /> Mobile Number
              </InfoLabel>
              <InfoValue>{order.customer_phone || "—"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <MapPin size={12} /> Place
              </InfoLabel>
              <InfoValue>{order.customer_place || "—"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <Calendar size={12} /> Order Date
              </InfoLabel>
              <InfoValue>{order.order_date}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </SectionCard>

        {order.events && order.events.length > 0 && (() => {
          const hasAnyEventDate = order.events.some((e: any) => Boolean(e.event_date && String(e.event_date).trim()));
          const hasAnyEventTime = order.events.some((e: any) => Boolean(formatTimeToAMPM(e.event_time)));
          return (
            <SectionCard>
              <CardHeader>
                <CardIcon $tone="#35b7c8">
                  <Calendar size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Event Details</CardTitle>
                  <CardSubtitle>
                    {order.events.length} event
                    {order.events.length !== 1 ? "s" : ""} scheduled
                  </CardSubtitle>
                </div>
              </CardHeader>
              <TableWrap>
                <PaymentsTable $minWidth={520}>
                  <thead>
                    <tr>
                      {hasAnyEventDate && (
                        <th>
                          <span>Event Date</span>
                        </th>
                      )}
                      {hasAnyEventTime && (
                        <th>
                          <span>Event Time</span>
                        </th>
                      )}
                      <th>
                        <span>Event Type</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.events.map((e: any, idx: number) => {
                      const hasDate = Boolean(e.event_date && String(e.event_date).trim());
                      const formattedTime = formatTimeToAMPM(e.event_time);
                      return (
                        <tr key={idx}>
                          {hasAnyEventDate && <td>{hasDate ? e.event_date : ""}</td>}
                          {hasAnyEventTime && <td>{formattedTime || ""}</td>}
                          <td>{e.event_type || ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </SectionCard>
          );
        })()}

        {order.resources && order.resources.length > 0 && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#f09a3e">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Resources & Products</CardTitle>
                <CardSubtitle>Line items included in this order</CardSubtitle>
              </div>
            </CardHeader>
            <TableWrap>
              <PaymentsTable $minWidth={640}>
                <thead>
                  <tr>
                    <th>
                      <span>Resource Name</span>
                    </th>
                    <th>
                      <span>Type</span>
                    </th>
                    <th>
                      <span>Qty</span>
                    </th>
                    {!order.without_price && (
                      <>
                        <th>
                          <span>Unit Price</span>
                        </th>
                        <th>
                          <span>Amount</span>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {order.resources.map((r: any, idx: number) => (
                    <tr key={idx}>
                      <td>{r.resource_name}</td>
                      <td style={{ textTransform: "capitalize" }}>
                        {r.resource_type}
                      </td>
                      <td>{r.quantity}</td>
                      {!order.without_price && (
                        <>
                          <td>
                            <AmountCell>
                              ₹
                              {Number(r.unit_price).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                          <td>
                            <AmountCell>
                              ₹
                              {(
                                r.quantity * Number(r.unit_price)
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </SectionCard>
        )}

        {order.fix_charge_items && order.fix_charge_items.length > 0 && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#6366f1">
                <DollarSign size={18} />
              </CardIcon>
              <div>
                <CardTitle>Fixed Charges</CardTitle>
                <CardSubtitle>Additional fixed line items</CardSubtitle>
              </div>
            </CardHeader>
            <TableWrap>
              <PaymentsTable $minWidth={520}>
                <thead>
                  <tr>
                    <th>
                      <span>Charge Name</span>
                    </th>
                    <th>
                      <span>Qty</span>
                    </th>
                    {!order.without_price && (
                      <>
                        <th>
                          <span>Unit Price</span>
                        </th>
                        <th>
                          <span>Amount</span>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {order.fix_charge_items.map((fc: any, idx: number) => (
                    <tr key={idx}>
                      <td>{fc.charge_name}</td>
                      <td>{fc.quantity}</td>
                      {!order.without_price && (
                        <>
                          <td>
                            <AmountCell>
                              ₹
                              {Number(fc.unit_price).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                          <td>
                            <AmountCell>
                              ₹
                              {(
                                fc.quantity * Number(fc.unit_price)
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </AmountCell>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </SectionCard>
        )}

        {order.gifts && order.gifts.length > 0 && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#e44862">
                <Gift size={18} />
              </CardIcon>
              <div>
                <CardTitle>Gifts</CardTitle>
                <CardSubtitle>Complimentary items</CardSubtitle>
              </div>
            </CardHeader>
            <TableWrap>
              <PaymentsTable $minWidth={360}>
                <thead>
                  <tr>
                    <th>
                      <span>Gift Name</span>
                    </th>
                    <th>
                      <span>Qty</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order.gifts.map((g: any, idx: number) => (
                    <tr key={idx}>
                      <td>{g.gift_name}</td>
                      <td>{g.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </SectionCard>
        )}

        {order.album_detail && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#c94daa">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Album Details</CardTitle>
                <CardSubtitle>Album specifications</CardSubtitle>
              </div>
            </CardHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Album Type</InfoLabel>
                <InfoValue>{order.album_detail.album_type_name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Width × Height</InfoLabel>
                <InfoValue>
                  {order.album_detail.album_width} ×{" "}
                  {order.album_detail.album_height} inches
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Album Paper</InfoLabel>
                <InfoValue>{order.album_detail.album_paper_name}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Album Pages</InfoLabel>
                <InfoValue>{order.album_detail.album_pages}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        )}

        {order.product_info_description && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#35b7c8">
                <FileText size={18} />
              </CardIcon>
              <div>
                <CardTitle>Product Information</CardTitle>
                <CardSubtitle>Description notes</CardSubtitle>
              </div>
            </CardHeader>
            <NotesBlock>{order.product_info_description}</NotesBlock>
          </SectionCard>
        )}

        {hasOptionalServices && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#f09a3e">
                <Layers size={18} />
              </CardIcon>
              <div>
                <CardTitle>Optional Services</CardTitle>
                <CardSubtitle>Add-ons and extras</CardSubtitle>
              </div>
            </CardHeader>
            <InfoGrid>
              {(order.album_detail.opt_album_page_cost ||
                Number(order.album_detail.opt_album_page_price) > 0) && (
                <InfoItem>
                  <InfoLabel>Album Extra Page Sheet</InfoLabel>
                  <InfoValue>
                    {order.album_detail.opt_album_page_cost || "Extra Sheet"}{" "}
                    {Number(order.album_detail.opt_album_page_price) > 0 &&
                      `(₹${order.album_detail.opt_album_page_price})`}
                  </InfoValue>
                </InfoItem>
              )}
              {(order.album_detail.opt_video_cost ||
                Number(order.album_detail.opt_video_price) > 0) && (
                <InfoItem>
                  <InfoLabel>Extra Video</InfoLabel>
                  <InfoValue>
                    {order.album_detail.opt_video_cost || "Extra Video"}{" "}
                    {Number(order.album_detail.opt_video_price) > 0 &&
                      `(₹${order.album_detail.opt_video_price})`}
                  </InfoValue>
                </InfoItem>
              )}
              {(Number(order.album_detail.opt_pad_price_from) > 0 ||
                Number(order.album_detail.opt_pad_price_to) > 0) && (
                <InfoItem>
                  <InfoLabel>Album Pad Price Range</InfoLabel>
                  <InfoValue>
                    ₹{order.album_detail.opt_pad_price_from || 0} TO ₹
                    {order.album_detail.opt_pad_price_to || 0}
                  </InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
          </SectionCard>
        )}

        {order.add_terms_conditions !== false && order.terms_and_conditions && (
          <SectionCard>
            <CardHeader>
              <CardIcon $tone="#c94daa">
                <FileText size={18} />
              </CardIcon>
              <div>
                <CardTitle>Terms & Conditions</CardTitle>
                <CardSubtitle>Policy terms for this order</CardSubtitle>
              </div>
            </CardHeader>
            <NotesBlock style={{ whiteSpace: "pre-wrap" }}>{order.terms_and_conditions}</NotesBlock>
          </SectionCard>
        )}

        {!order.without_price && (
          <SummaryCard>
            <SummaryRow>
              <span>Discount</span>
              <span>{order.discount_percent || 0}%</span>
            </SummaryRow>
            <SummaryRow>
              <span>Tax</span>
              <span>{order.tax_percent || 0}%</span>
            </SummaryRow>
            <SummaryTotal>
              <span>Grand Total</span>
              <span>
                ₹
                {calculatedGrandTotal.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </SummaryTotal>
          </SummaryCard>
        )}
      </ScrollBody>

      {modalShareOrder && (
        <OrderShareModal
          order={modalShareOrder}
          onClose={() => setModalShareOrder(null)}
          onShareOfficial={handleOfficialShare}
          loading={sharingWhatsApp}
        />
      )}

      <DocumentPrintTemplate
        printRef={printRef}
        activeDoc={
          order ? { ...order, grand_total: String(calculatedGrandTotal) } : null
        }
        user={user}
        studioLogo={studio_logo || business?.studio_logo}
        studioName={business?.studio_name}
        studioAddress={business?.address}
        type="order"
      />

      {deleteConfirm && (
        <DeletePopup
          text={`Are you sure you want to delete order ${order.order_no}?`}
          buttonText="Delete"
          cancel={() => setDeleteConfirm(false)}
          Delete={handleConfirmDelete}
        />
      )}
    </PageContainer>
  );
};

export default OrderDetailsPage;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -60px;
  right: -4%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.28) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 480px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  color: rgba(23, 23, 23, 0.72);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateY(-1px);
  }
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 4px;
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIcon = styled.div<{ $tone?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone || "#c94daa"};
  background: ${(p) => `${p.$tone || "#c94daa"}14`};
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const CardSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionDivider = styled.div`
  width: 1px;
  height: 28px;
  background: rgba(23, 23, 23, 0.1);
  flex-shrink: 0;
  @media ${device.tablet} {
    display: none;
  }
`;

const ActionSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;

const SoftBtn = styled.button<{ $tone: string; $solid?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$solid ? "transparent" : `${p.$tone}33`)};
  background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}14`)};
  color: ${(p) => (p.$solid ? "#fff" : p.$tone)};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${(p) => (p.$solid ? `0 6px 16px ${p.$tone}40` : "none")};
  transition: all 0.15s ease;

  svg {
    flex-shrink: 0;
    display: block;
  }

  &:hover:not(:disabled) {
    background: ${(p) => (p.$solid ? p.$tone : `${p.$tone}22`)};
    border-color: ${(p) => (p.$solid ? "transparent" : `${p.$tone}55`)};
    transform: translateY(-1px);
    box-shadow: ${(p) =>
      p.$solid ? `0 8px 18px ${p.$tone}48` : `0 4px 12px ${p.$tone}18`};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const InfoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.48);
`;

const InfoValue = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
`;

const NotesBlock = styled.div`
  white-space: pre-wrap;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.65;
  padding: 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const SummaryCard = styled.div`
  margin-left: auto;
  width: min(100%, 340px);
  padding: 20px 22px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.1) 0%,
    rgba(206, 86, 176, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.14);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px dashed rgba(201, 77, 170, 0.25);
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #c94daa;
`;

const SpinIcon = styled(Loader2)`
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
